import { Injectable, NgZone } from '@angular/core';
import { merge, combineLatest, OperatorFunction, of } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, tap, mergeMap, share, take, takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { ButtonCode } from './buttoncode.interface';
import { ConsoleService } from './console.service';

const BSControlPort = window['BSControlPort'];

function runInZone<T>(zone: NgZone): OperatorFunction<T, T> {
  return (source) => {
    return new Observable(observer => {
      const onNext = (value: T) => zone.run(() => observer.next(value));
      const onError = (e: any) => zone.run(() => observer.error(e));
      const onComplete = () => zone.run(() => observer.complete());
      return source.subscribe(onNext, onError, onComplete);
    });
  };
}

function fromBSEvent(target: any, title: string): Observable<ButtonCode> {
  return new Observable(observer => {
    target[title] = (event) => {
      observer.next(event.code as ButtonCode);
    }
  });
}

enum ButtonEventType {
  Up = 'up',
  Down = 'down'
};

interface ButtonEvent {
  type: ButtonEventType,
  code: ButtonCode,
}

@Injectable()
export class GpioService {
  public buttonEvents$: Observable<ButtonEvent>;

  constructor(private zone: NgZone, private consoleService: ConsoleService) {
    if (BSControlPort) {
      // Setup a new GPIO port with inputs on pins zero through five.
      const port = new BSControlPort("BrightSign");

      // Configure the pins as inputs.
      for (var pin = 0; pin < 6; pin++) {
        port.ConfigureAsInput(pin);
      }

      const buttonDown$ = fromBSEvent(port, 'oncontroldown').pipe(
        runInZone(this.zone),
        map(code => ({
          type: ButtonEventType.Down,
          code: code
        } as ButtonEvent)),
      );
      const buttonUp$ = fromBSEvent(port, 'oncontrolup').pipe(
        runInZone(this.zone),
        map(code => ({
          type: ButtonEventType.Up,
          code: code
        } as ButtonEvent)),
      );

      this.buttonEvents$ = merge(buttonDown$, buttonUp$).pipe(
        distinctUntilChanged((a, b) => a.code === b.code && a.type === b.type),
        tap(event => this.consoleService.log(JSON.stringify(event))),
        share()
      );

    } else {
      this.consoleService.log('BSControPort not available');

      this.buttonEvents$ = new Observable();
    }
  }

  all(buttons: ButtonCode[], hold: number = null): Observable<ButtonEvent[]> {
    const createButtonPressStream = (button: ButtonCode) =>
      this.buttonEvents$.pipe(filter((event) => event.code === button.valueOf()));

    // Create Event Stream for every ButtonCode in shortcut
    const buttonCodeEvents$ = buttons.map((button) => createButtonPressStream(button));

    // Emit when specified buttons are pressed (buttondown).
    // Emit only when all specified buttons are pressed at the same time.
    // More on combineLatest below
    const combined$ = combineLatest(buttonCodeEvents$);
    const pressed$ = combined$.pipe(
      filter((array) => array.every((event) => event.type === ButtonEventType.Down))
    );
    const released$ = combined$.pipe(
      filter((array) => array.every((event) => event.type === ButtonEventType.Up))
    );

    if (hold === null) {
      return pressed$;
    } else {
      return pressed$.pipe(
        mergeMap(event => of(event).pipe(take(1), delay(hold), takeUntil(released$)))
      );
    }
  }


  any(buttons: ButtonCode[]): Observable<ButtonEvent> {
    const buttonCodeEvents$ = buttons.map((button) => this.single(button));
    return merge(...buttonCodeEvents$);
  }


  single(code: ButtonCode): Observable<ButtonEvent> {
    return this.buttonEvents$.pipe(
      filter(event => event.code === code.valueOf() && event.type === ButtonEventType.Down)
    );
  }
}
